/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import ComponentBase from '../../ComponentBase';
import CLog from '../../ComponentLogger';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

@cc._decorator.ccclass
export default class MutatorComponentBase extends ComponentBase
{
    @property()
    protected pathParts: number = 4;

    @property([cc.Node])
    protected excludeNodes: cc.Node[] = [];

    @property()
    protected shouldLogSkipped: boolean = false;

    @property()
    protected shouldModify: boolean = false;

    private _warningCount: number = 0;
    public get WarningCount(): number { return this._warningCount; }

    protected execute(node: cc.Node, handlers: ((node: cc.Node) => boolean)[], overideStartLog: string = null): void
    {
        if (!node)
        {
            CLog.error('No target set.');
            return;
        }

        this._warningCount = 0;

        let start: number = Date.now();
        cc.warn(overideStartLog ? overideStartLog : 'Beginning node search...');

        this.applyNodeRecursively(node, handlers);

        cc.warn(`Finished in ${(Date.now() - start)}ms with ${this._warningCount}.`);

        this.shouldModify = false;
    }

    protected applyNodeRecursively(
        node: cc.Node,
        nodeHandlers: ((node: cc.Node) => boolean)[]): void
    {
        let hasChanged: boolean = false;
        nodeHandlers.forEach(nodeHandler =>
            {
                hasChanged = nodeHandler(node) || hasChanged;
            }, this);

        if (!hasChanged)
        {
            this.logSkipped(node);
        }

        if (node.children)
        {
            node.children
                .filter(child => !this.excludeNodes.find(n => n === child))
                .forEach(child => this.applyNodeRecursively(child, nodeHandlers));
        }
    }

    protected log(target: cc.Node, message: string): void
    {
        cc.log(this.getNodeLogMessageWithTag(target, message));
    }

    protected logSkipped(target: cc.Node): void
    {
        if (!this.shouldLogSkipped) { return; }

        this.log(target, 'SKIPPED');
    }

    protected warn(target: cc.Node, message: string): void
    {
        cc.warn(this.getNodeLogMessageWithTag(target, message));
        this._warningCount++;
    }

    protected error(target: cc.Node, message: string): void
    {
        cc.error(this.getNodeLogMessageWithTag(target, message));
    }

    protected getNodeLogMessageWithTag(target: cc.Node, message: string): string
    {
        if (!target) { return message; }
        return `${target.uuid} - [ ${this.getShallowPath(target)} ] - ${message}`;
    }

    protected getShallowPath(target: cc.Node): string
    {
        if (this.pathParts === 1) { return target.name; }

        let canvas: cc.Node = cc.find('Canvas');

        let count: number = this.pathParts - 1;

        let path: string = target.name;
        let current: cc.Node = target.parent;
        while (current)
        {
            path = `${current.name}/${path}`;

            if (current === canvas
                || --count < 1) { break; }

            current = current.parent;
        }

        return path;
    }
}
